import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { Toaster, toast } from 'sonner';

type ToastType = 'success' | 'error' | 'info' | 'warning';

type ToastCtx = {
  show: (msg: string, type?: ToastType) => void;
};

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const show = useCallback((message: string, type: ToastType = 'success') => {
    if (type === 'error') toast.error(message);
    else if (type === 'info') toast.info(message);
    else if (type === 'warning') toast.warning(message);
    else toast.success(message);
  }, []);

  return (
    <Ctx.Provider value={{ show }}>
      {children}
      <Toaster
        position="top-center"
        dir="rtl"
        richColors
        closeButton
        toastOptions={{
          style: { fontFamily: 'Cairo, sans-serif', direction: 'rtl' },
        }}
      />
    </Ctx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

export function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light';
    return (localStorage.getItem('theme') as 'light' | 'dark') || 'light';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  return { theme, setTheme };
}
